import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { NewCustomerToday, ReturningCustomerToday } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { IconInbox } from '@/components/Icons';
import { SectionCard } from '@/components/SectionCard';
import { DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { usePortal } from '@/features/portal/PortalContext';
import { fetchCustomersToday, portalKeys } from '@/features/portal/api';
import { formatCount, formatMoney } from '@/lib/format';

/** A customer's name, linking to their detail page. */
function NameLink({ customerKey, name }: { customerKey: string; name: string }) {
  return (
    <Link className="link-primary font-medium" to={`/portal/customers/${encodeURIComponent(customerKey)}`}>
      {name || '—'}
    </Link>
  );
}

/**
 * One request feeds both cards (React Query shares it by key), so showing them
 * together or apart costs the same. A customer is in at most one of them —
 * someone added today who also buys today is NEW only.
 */
function useCustomersToday(date: string) {
  const { currency } = usePortal();
  const query = useQuery({
    queryKey: portalKeys.customersToday(date),
    queryFn: ({ signal }) => fetchCustomersToday(date, signal),
    // Customers appear as tills sync; keep the cards fresh without a reload.
    refetchInterval: 120_000,
  });
  return { query, cur: query.data?.currency || currency };
}

const SKELETON = <TableSkeleton rows={4} columns={4} />;

/** Who the business met for the first time today, with the number to reach them. */
export function NewCustomersTodayCard({ date }: { date: string }) {
  const { query, cur } = useCustomersToday(date);

  const columns = useMemo<Column<NewCustomerToday>[]>(
    () => [
      {
        key: 'name',
        header: 'Name',
        cell: (row) => <NameLink customerKey={row.key} name={row.name} />,
      },
      { key: 'phone', header: 'Phone', cell: (row) => row.phone || '—' },
      {
        key: 'branches',
        header: 'Branch',
        visibility: 'hidden sm:table-cell',
        cell: (row) => (row.branches.length ? row.branches.join(', ') : '—'),
      },
      { key: 'addedAt', header: 'Added', cell: (row) => row.addedAt || '—' },
      {
        key: 'spentToday',
        header: 'Spent today',
        align: 'right',
        cell: (row) => (row.ordersToday > 0 ? formatMoney(row.spentToday, cur) : '—'),
      },
    ],
    [cur],
  );

  return (
    <SectionCard
      headingId="customers-new-today"
      title="New customers today"
      count={query.data?.newCustomers.length}
      description="First time the business has seen them — name and number, whether or not they bought."
    >
      <AsyncSection
        state={query}
        skeleton={SKELETON}
        isEmpty={(d) => d.newCustomers.length === 0}
        emptyIcon={<IconInbox className="h-5 w-5" />}
        emptyTitle="No new customers today"
        emptyDescription="Customers added at any till today will appear here."
      >
        {(d) => (
          <DataTable
            caption="Customers the business saw for the first time today"
            columns={columns}
            rows={d.newCustomers}
            rowKey={(r) => r.key}
          />
        )}
      </AsyncSection>
    </SectionCard>
  );
}

/** Existing customers who came back and bought today. */
export function ReturningCustomersTodayCard({ date }: { date: string }) {
  const { query, cur } = useCustomersToday(date);

  const columns = useMemo<Column<ReturningCustomerToday>[]>(
    () => [
      {
        key: 'name',
        header: 'Name',
        cell: (row) => <NameLink customerKey={row.key} name={row.name} />,
      },
      { key: 'phone', header: 'Phone', cell: (row) => row.phone || '—' },
      {
        key: 'ordersToday',
        header: 'Bills today',
        align: 'right',
        cell: (row) => formatCount(row.ordersToday),
      },
      {
        key: 'spentToday',
        header: 'Spent today',
        align: 'right',
        cell: (row) => formatMoney(row.spentToday, cur),
      },
      {
        key: 'visits',
        header: 'Visits',
        align: 'right',
        visibility: 'hidden sm:table-cell',
        cell: (row) => formatCount(row.visits),
      },
      {
        key: 'previousVisit',
        header: 'Last visit before',
        visibility: 'hidden md:table-cell',
        cell: (row) => row.previousVisit ?? '—',
      },
    ],
    [cur],
  );

  return (
    <SectionCard
      headingId="customers-returning-today"
      title="Returning customers today"
      count={query.data?.returning.length}
      description="Existing customers who bought today."
    >
      <AsyncSection
        state={query}
        skeleton={SKELETON}
        isEmpty={(d) => d.returning.length === 0}
        emptyIcon={<IconInbox className="h-5 w-5" />}
        emptyTitle="No returning customers yet today"
        emptyDescription="Customers who have bought before and buy again today will appear here."
      >
        {(d) => (
          <>
            <DataTable
              caption="Existing customers who bought today"
              columns={columns}
              rows={d.returning}
              rowKey={(r) => r.key}
            />
            {d.walkInOrders > 0 ? (
              // Walk-ins belong to nobody. Said here so the two lists can be
              // reconciled against today's sales figure.
              <p className="border-t border-slate-200 px-5 py-3 text-xs text-slate-500">
                Plus {formatCount(d.walkInOrders)} walk-in bill{d.walkInOrders === 1 ? '' : 's'} (
                {formatMoney(d.walkInSpend, cur)}) with no customer attached.
              </p>
            ) : null}
          </>
        )}
      </AsyncSection>
    </SectionCard>
  );
}
